var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createMachine, assign, interpret } from 'xstate';
import { toSCXMLEvent, toEventObject } from 'xstate/lib/utils';
var serviceMap = new Map();
export function createDevTools() {
    var services = new Set();
    var serviceListeners = new Set();
    window.__xstate__ = {
        services: services,
        register: function (service) {
            services.add(service);
            serviceMap.set(service.sessionId, service);
            serviceListeners.forEach(function (listener) { return listener(service); });
            service.onStop(function () {
                services.delete(service);
                serviceMap.delete(service.sessionId);
            });
        },
        unregister: function (service) {
            services.delete(service);
            serviceMap.delete(service.sessionId);
        },
        onRegister: function (listener) {
            serviceListeners.add(listener);
            services.forEach(function (service) { return listener(service); });
            return {
                unsubscribe: function () {
                    serviceListeners.delete(listener);
                }
            };
        }
    };
}
export var inspectMachine = createMachine({
    initial: 'pendingConnection',
    context: {
        client: undefined
    },
    states: {
        pendingConnection: {},
        connected: {
            on: {
                'service.state': {
                    actions: function (ctx, e) { return ctx.client.send(e); }
                },
                'service.event': {
                    actions: function (ctx, e) { return ctx.client.send(e); }
                },
                'service.register': {
                    actions: function (ctx, e) { return ctx.client.send(e); }
                },
                'service.stop': {
                    actions: function (ctx, e) { return ctx.client.send(e); }
                },
                'xstate.event': {
                    actions: function (_, e) {
                        var event = e.event;
                        var scxmlEventObject = JSON.parse(event);
                        var service = serviceMap.get(scxmlEventObject.origin);
                        service === null || service === void 0 ? void 0 : service.send(JSON.parse(event));
                    }
                },
                unload: {
                    actions: function (ctx) {
                        ctx.client.send({ type: 'xstate.disconnect' });
                    }
                },
                disconnect: 'pendingConnection'
            }
        }
    },
    on: {
        'xstate.inspecting': {
            target: '.connected',
            actions: [
                assign({ client: function (_, e) { return e.client; } }),
                function (ctx) {
                    globalThis.__xstate__.services.forEach(function (service) {
                        ctx.client.send({
                            type: 'service.register',
                            machine: JSON.stringify(service.machine),
                            state: JSON.stringify(service.state || service.initialState),
                            sessionId: service.sessionId
                        });
                    });
                }
            ]
        }
    }
});
var defaultInspectorOptions = {
    url: 'https://statecharts.io/inspect',
    iframe: function () { return document.querySelector('iframe[data-xstate]'); }
};
export function inspect(options) {
    createDevTools();
    var _a = __assign(__assign({}, defaultInspectorOptions), options), iframe = _a.iframe, url = _a.url;
    var resolvedIframe = typeof iframe === 'function' ? iframe() : iframe;
    var targetWindow;
    var inspectService = interpret(inspectMachine).start();
    if (resolvedIframe === null) {
        console.warn('No suitable <iframe> found to embed the inspector. Please pass an <iframe> element to `inspect(iframe)` or create an <iframe data-xstate></iframe> element.');
        return { disconnect: function () { return void 0; } };
    }
    var client;
    var messageHandler = function (event) {
        if (typeof event.data === 'object' &&
            event.data !== null &&
            'type' in event.data) {
            if (resolvedIframe && !targetWindow) {
                targetWindow = resolvedIframe.contentWindow;
            }
            if (!client) {
                client = {
                    send: function (e) {
                        targetWindow.postMessage(e, url);
                    }
                };
            }
            inspectService.send(__assign(__assign({}, event.data), { client: client }));
        }
    };
    window.addEventListener('message', messageHandler);
    window.addEventListener('unload', function () {
        inspectService.send({ type: 'unload' });
    });
    if (resolvedIframe === false) {
        targetWindow = window.open(url, 'xstateinspector');
    }
    globalThis.__xstate__.onRegister(function (service) {
        var _a;
        inspectService.send({
            type: 'service.register',
            machine: JSON.stringify(service.machine),
            state: JSON.stringify(service.state || service.initialState),
            sessionId: service.sessionId,
            id: service.id,
            parent: (_a = service.parent) === null || _a === void 0 ? void 0 : _a.sessionId
        });
        inspectService.send({
            type: 'service.event',
            event: JSON.stringify((service.state || service.initialState)._event),
            sessionId: service.sessionId
        });
        // monkey-patch service.send so that we know when an event was sent
        // to a service *before* it is processed, since other events might occur
        // while the sent one is being processed, which throws the order off
        var originalSend = service.send.bind(service);
        service.send = function inspectSend(event, payload) {
            inspectService.send({
                type: 'service.event',
                event: JSON.stringify(toSCXMLEvent(toEventObject(event, payload))),
                sessionId: service.sessionId
            });
            return originalSend(event, payload);
        };
        service.subscribe(function (state) {
            inspectService.send({
                type: 'service.state',
                state: JSON.stringify(state),
                sessionId: service.sessionId
            });
        });
        service.onStop(function () {
            inspectService.send({
                type: 'service.stop',
                sessionId: service.sessionId
            });
        });
    });
    if (resolvedIframe) {
        resolvedIframe.addEventListener('load', function () {
            targetWindow = resolvedIframe.contentWindow;
        });
        resolvedIframe.setAttribute('src', url);
    }
    return {
        disconnect: function () {
            inspectService.send('disconnect');
            window.removeEventListener('message', messageHandler);
        }
    };
}
